'use client'

import React from 'react'
import { cn } from '@/utilities/ui'
import { Media } from '@/components/Media'
import Link from 'next/link'
import { Facebook, Instagram } from 'lucide-react'
import { TypedLocale } from 'payload'
import AnimatedText from '@/components/Antonie/Components/animated-text'
import { SocialMedia } from '@/payload-types'

type Props = {
  title?: string
  description?: string
  maxItems?: number
  enableGutter?: boolean
  className?: string
  locale: TypedLocale
  socialMediaEntries: SocialMedia[]
}

export const SocialMediaBlock: React.FC<Props> = (props) => {
  const { title, description, enableGutter = true, className, socialMediaEntries } = props

  if (!socialMediaEntries || socialMediaEntries.length === 0) {
    return null
  }

  const getPlatformIcon = (platform: string, className?: string) => {
    switch (platform) {
      case 'facebook':
        return <Facebook className={cn('w-4 h-4', className)} />
      case 'instagram':
        return <Instagram className={cn('w-4 h-4', className)} />
      case 'both':
        return (
          <div className="flex gap-1">
            <Facebook className={cn('w-4 h-4', className)} />
            <Instagram className={cn('w-4 h-4', className)} />
          </div>
        )
      default:
        return null
    }
  }

  const getPlatformUrl = (entry: SocialMedia) => {
    if (entry.platform === 'facebook' && entry.facebookUrl) {
      return entry.facebookUrl
    }
    if (entry.platform === 'instagram' && entry.instagramUrl) {
      return entry.instagramUrl
    }
    if (entry.platform === 'both') {
      return entry.facebookUrl || entry.instagramUrl
    }
    return null
  }

  return (
    <div
      className={cn(
        'py-12',
        {
          container: enableGutter,
        },
        className,
      )}
    >
      {(title || description) && (
        <div className="mb-12 text-center">
          {title && <h2 className="mb-4 text-3xl font-bold">{title}</h2>}
          {description && (
            <p className="mx-auto max-w-2xl text-lg text-muted-foreground">{description}</p>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {socialMediaEntries.map((entry) => {
          const platformUrl = getPlatformUrl(entry)

          return (
            <div
              key={entry.id}
              className="group relative overflow-hidden  border border-border transition-all duration-300 hover:shadow-lg"
            >
              <div className="aspect-square overflow-hidden">
                <Media
                  resource={entry.image}
                  imgClassName="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                />
              </div>

              <div className="absolute inset-0 bg-black/0 transition-colors duration-300 group-hover:bg-black/20" />

              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <AnimatedText
                      text={entry.title}
                      className="mb-1 line-clamp-1 text-sm font-semibold text-white"
                      type="h2"
                    />
                  </div>

                  {platformUrl && (
                    <Link
                      href={platformUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-white transition-colors duration-200 hover:text-primary"
                    >
                      {getPlatformIcon(entry.platform, 'text-white')}
                    </Link>
                  )}
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
